'use client';

/**
 * ContactSection — email / phone / type rendering for rail cards.
 *
 * Mode4-native parallel of `<ContactSection />` under
 * copilot/details/sections/. Lives under rail-insights/sections/ so
 * it survives the CopilotPanel retirement (same doctrine as
 * GallerySection).
 *
 * Auto-hides when none of the three fields carry a value.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { Mail, Phone, Building2 } from 'lucide-react';
import { text } from '@/lib/design/tokens';

interface ContactSectionProps {
  data: Record<string, unknown>;
  label?: string | null;
}

export function ContactSection({ data, label }: ContactSectionProps) {
  const email = (data.email ?? null) as string | null;
  const phone = (data.phone ?? null) as string | null;
  const type = (data.type ?? data.subtype ?? null) as string | null;

  if (!email && !phone && !type) return null;

  return (
    <div>
      <p className={text.eyebrow}>{label || 'Contact'}</p>
      <div className={`${text.body} space-y-1`}>
        {email && (
          <div className="flex items-center gap-1.5">
            <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
            <a href={`mailto:${email}`} className="hover:underline truncate">
              {email}
            </a>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-1.5">
            <Phone className="h-3 w-3 text-muted-foreground shrink-0" />
            <a href={`tel:${phone}`} className="hover:underline">
              {phone}
            </a>
          </div>
        )}
        {type && (
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className="capitalize">{type.toLowerCase()}</span>
          </div>
        )}
      </div>
    </div>
  );
}
